import type { ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { BookOpen, CheckSquare, ClipboardCheck, Clock, Edit, FileText, MessageSquare, Monitor, Users, Video, ChevronRight, Bell } from 'lucide-react';
import { getHome } from '@/api/teacher';
import { QueryState } from '@/components/kit/ui';
import { unwrap } from '@/components/kit/data';
import { NoticesPanel } from '@/components/kit/panels';
import { useAuth } from '@/stores/auth';
import type { MyClass } from '../shared';

interface TodayRow { id: number; class: string; subject: string; period: number; starts_at: string; ends_at: string; room: string | null }
interface Teaching {
  lessons: { published: number; drafts: number; completion: number };
  live_today: { id: number; title: string; starts_at: string; state: string }[];
  lesson_notes: { submitted: number; changes_requested: number; approved: number };
  students: number;
}
interface Home { classes: MyClass[]; today: TodayRow[]; clocked_in: boolean; clocked_out: boolean; late_today: boolean; to_grade: number; open_assignments: number; unread_notifications: number; teaching?: Teaching }

const time = (s: string) => new Date(s).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

function Tile({ to, label, value, hint, tone = 'text-slate-900', icon: Icon }: { to: string; label: string; value: ReactNode; hint?: string; tone?: string; icon: any }) {
  return (
    <Link to={to} className="bg-white rounded-2xl border border-slate-200 p-3.5 hover:border-slate-300 transition block">
      <div className="flex items-center justify-between"><p className="text-xs text-slate-500 truncate">{label}</p><Icon className="w-4 h-4 text-slate-400" /></div>
      <p className={`text-2xl font-extrabold mt-1 ${tone}`}>{value}</p>
      {hint && <p className="text-[11px] text-slate-500 truncate">{hint}</p>}
    </Link>
  );
}

function Panel({ title, to, link, children }: { title: string; to?: string; link?: string; children: ReactNode }) {
  return (
    <section className="bg-white rounded-2xl border border-slate-200">
      <div className="flex items-center justify-between px-4 pt-3.5 pb-2"><h2 className="font-bold text-slate-900">{title}</h2>{to && <Link to={to} className="text-xs font-semibold text-[#0A2540] inline-flex items-center">{link ?? 'Open'}<ChevronRight className="w-3.5 h-3.5" /></Link>}</div>
      <div className="px-4 pb-4">{children}</div>
    </section>
  );
}

export function DashboardPage() {
  const name = useAuth((s) => s.user?.name)?.split(' ')[0];
  const q = useQuery({ queryKey: ['teacher-home'], queryFn: () => getHome().then((r) => unwrap<Home>(r) ?? null) });
  const h = q.data;
  const t = h?.teaching;
  const form = h?.classes.filter((c) => c.is_form_teacher) ?? [];
  const hour = new Date().getHours();

  return (
    <div className="space-y-4 max-w-6xl">
      <div>
        <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500">{new Date().toLocaleDateString([], { weekday: 'long', day: 'numeric', month: 'long' })}</p>
        <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900">Good {hour < 12 ? 'morning' : hour < 17 ? 'afternoon' : 'evening'}{name ? `, ${name}` : ''}</h1>
      </div>
      <QueryState q={q}>
        {h && (
          <>
            <Link to="../clock-in" className={`flex items-center gap-3 rounded-2xl px-4 py-3 border ${h.clocked_in ? 'bg-green-50 border-green-200 text-green-900' : 'bg-amber-50 border-amber-200 text-amber-900'}`}>
              <Clock className="w-5 h-5 shrink-0" />
              <span className="text-sm font-medium flex-1">{h.clocked_out ? 'You have clocked out for today.' : h.clocked_in ? (h.late_today ? 'Clocked in — marked late today.' : 'You are clocked in.') : 'You have not clocked in today. Tap to clock in.'}</span>
              <ChevronRight className="w-4 h-4" />
            </Link>

            {!!t?.live_today.length && (
              <div className="rounded-2xl bg-[#0A2540] text-white p-4 space-y-2">
                <p className="text-xs font-bold uppercase tracking-wider text-white/60">Live classes today</p>
                {t.live_today.map((l) => (
                  <Link key={l.id} to="../live-classes" className="flex items-center gap-3 bg-white/10 rounded-xl px-3 py-2.5">
                    <Video className="w-4 h-4 shrink-0" />
                    <span className="flex-1 min-w-0 truncate text-sm font-semibold">{l.title}</span>
                    <span className="text-xs text-white/70">{l.state === 'live' ? 'Live now' : l.state === 'open' ? 'Ready to start' : time(l.starts_at)}</span>
                  </Link>
                ))}
              </div>
            )}

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              <Tile to="../classes" icon={Users} label="My classes" value={h.classes.length} hint={`${t?.students ?? 0} students`} />
              <Tile to="../assignments" icon={FileText} label="To grade" value={h.to_grade} hint={`${h.open_assignments} open assignments`} tone={h.to_grade ? 'text-amber-600' : 'text-slate-900'} />
              <Tile to="../lessons" icon={BookOpen} label="Lessons published" value={t?.lessons.published ?? 0} hint={t ? `${t.lessons.drafts} drafts · ${t.lessons.completion}% done by students` : undefined} />
              <Tile to="../lesson-notes" icon={ClipboardCheck} label="Lesson notes" value={t?.lesson_notes.submitted ?? 0} hint={t?.lesson_notes.changes_requested ? `${t.lesson_notes.changes_requested} need changes` : `${t?.lesson_notes.approved ?? 0} approved`} tone={t?.lesson_notes.changes_requested ? 'text-red-600' : 'text-slate-900'} />
            </div>

            <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
              {[
                ['../attendance', 'Attendance', CheckSquare], ['../score-entry', 'Scores', Edit], ['../cbt-exams', 'CBT tests', Monitor],
                ['../lessons', 'New lesson', BookOpen], ['../messages', 'Messages', MessageSquare], ['../notifications', `Alerts${h.unread_notifications ? ` (${h.unread_notifications})` : ''}`, Bell],
              ].map(([to, l, I]: any) => (
                <Link key={l} to={to} className="bg-white rounded-2xl border border-slate-200 py-3 flex flex-col items-center gap-1.5 text-xs font-semibold text-slate-700 hover:border-slate-300"><I className="w-5 h-5 text-[#0A2540]" />{l}</Link>
              ))}
            </div>

            <div className="grid lg:grid-cols-2 gap-4">
              <Panel title="Today's timetable" to="../timetable" link="Full week">
                {!h.today?.length ? <p className="text-sm text-slate-500">No periods on your timetable today.</p> : (
                  <ul className="divide-y divide-slate-100">
                    {h.today.map((r) => (
                      <li key={r.id} className="py-2 flex items-center gap-3 text-sm">
                        <span className="font-mono text-xs text-slate-500 w-24 shrink-0">{r.starts_at?.slice(0, 5)}–{r.ends_at?.slice(0, 5)}</span>
                        <span className="flex-1 min-w-0 truncate"><b>{r.subject}</b> · {r.class}</span>
                        {r.room && <span className="text-xs text-slate-500">{r.room}</span>}
                      </li>
                    ))}
                  </ul>
                )}
              </Panel>
              <Panel title="My classes" to="../classes">
                {!h.classes.length ? <p className="text-sm text-slate-500">No classes assigned to you yet.</p> : (
                  <ul className="divide-y divide-slate-100">
                    {h.classes.map((c) => (
                      <li key={c.class_id} className="py-2 text-sm">
                        <div className="flex items-center gap-2"><b className="text-slate-900">{c.class}</b>{c.is_form_teacher && <span className="text-[10px] font-bold uppercase bg-blue-50 text-blue-700 rounded-full px-2 py-0.5">Form teacher</span>}<span className="ml-auto text-xs text-slate-500">{c.students} students</span></div>
                        <p className="text-xs text-slate-500 truncate">{c.subjects.map((s) => s.name).join(', ')}</p>
                      </li>
                    ))}
                  </ul>
                )}
                {form.length > 0 && <Link to="../remarks" className="mt-3 inline-flex text-xs font-semibold text-[#0A2540]">Write end-of-term remarks for {form.map((c) => c.class).join(', ')} →</Link>}
              </Panel>
            </div>
          </>
        )}
      </QueryState>
      <Panel title="Notices" to="../calendar" link="Calendar"><NoticesPanel /></Panel>
    </div>
  );
}
